import { quantileSggData, quantileEmdData } from '../../common/quantile';
import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { ViContext } from '../../context/ViContext';
import { MapContext } from '../../context/MapContext';
import GeoJSON from 'ol/format/GeoJSON';
import { extend } from 'ol/extent';

import { Slider, SvgIcon } from '@mui/material';
import { getLayersByKey } from '../../common/layer';

function WeightedContent(props) {
	const {
		sidoCode,
		sggCode,
		emdCode,
		pop,
		prod,
		consume,
		setSidoCode,
		setSggCode,
		setEmdCode,
		setPop,
		setProd,
		setConsume,
		weightedInfo,
		setWeightedInfo,
		tabInfoArr,
		setTabInfoArr,
		selectedTab,
	} = useContext(ViContext);
	const { weightedMap, getFeatures, weightedMapLayers } =
		useContext(MapContext);
	const [sidoList, setSidoList] = useState([]);
	const [sggList, setSggList] = useState([]);
	const [emdList, setEmdList] = useState([]);
	const [sidoName, setSidoName] = useState('전국');
	const [sggName, setSggName] = useState('전체');
	const [emdName, setEmdName] = useState('전체');
	const [selectSggLegend, setSelectSggLegend] = useState(
		new Array(5).fill(true)
	);
	const [selectEmdLegend, setSelectEmdLegend] = useState(
		new Array(10).fill(true)
	);
	const [layerOpacity, setLayerOpacity] = useState(1);

	useEffect(() => {
		axios({
			method: 'GET',
			url: `${process.env.REACT_APP_CONTEXT_PATH}/interactive/api/vi/codeList.json`,
		})
			.then((res) => {
				setSidoList(res.data.data.data);
			})
			.catch((error) => {
				console.log(error);
			});
	}, []);

	useEffect(() => {
		setEmdList([]);

		if (sidoCode) {
			axios({
				method: 'GET',
				url: `${process.env.REACT_APP_CONTEXT_PATH}/interactive/api/vi/codeList.json?sido_code=${sidoCode}`,
			})
				.then((res) => {
					setSggList(res.data.data.data);
				})
				.catch((error) => {
					console.log(error);
				});
		} else {
			setSggList([]);
		}
	}, [sidoCode]);

	useEffect(() => {
		if (sggCode) {
			axios({
				method: 'GET',
				url: `${process.env.REACT_APP_CONTEXT_PATH}/interactive/api/vi/codeList.json?sgg_code=${sggCode}`,
			})
				.then((res) => {
					setEmdList(res.data.data.data);
				})
				.catch((error) => {
					console.log(error);
				});
		} else {
			setEmdList([]);
		}
	}, [sggCode]);

	useEffect(() => {
		setSidoName(tabInfoArr[selectedTab].sidoName);
		setSggName(tabInfoArr[selectedTab].sggName);
		setEmdName(tabInfoArr[selectedTab].emdName);
		setSidoCode(tabInfoArr[selectedTab].sidoCode);
		setSggCode(tabInfoArr[selectedTab].sggCode);
		setEmdCode(tabInfoArr[selectedTab].emdCode);

		setPop(tabInfoArr[selectedTab].pop);
		setConsume(tabInfoArr[selectedTab].consume);
		setProd(tabInfoArr[selectedTab].prod);

		async function fetchData() {
			let features;
			if (tabInfoArr[selectedTab].emdCode) {
				features = await getFeatures(tabInfoArr[selectedTab].emdCode, 'amd_cd');
			} else if (tabInfoArr[selectedTab].sggCode) {
				features = await getFeatures(tabInfoArr[selectedTab].sggCode, 'sgg_cd');
			} else if (tabInfoArr[selectedTab].sidoCode) {
				features = await getFeatures(
					tabInfoArr[selectedTab].sidoCode,
					'sido_cd'
				);
			}
			extentFeatures(features);
		}

		if (tabInfoArr[selectedTab].sidoCode !== '') {
			fetchData();
		}
	}, [tabInfoArr[selectedTab]]);

	useEffect(() => {
		if (weightedMap && weightedMapLayers) {
			const layers = weightedMapLayers;
			if (emdCode !== '') {
				// 읍면동
				const params = {
					CQL_FILTER: `amd_cd like '${emdCode.substring(0, 5)}%'`,
				};
				if (!selectEmdLegend.includes(false)) {
					params.STYLES = 'vi_emd_region';
					params.env = `formular:(m12*${pop}+m34*${consume}+m56*${prod})/${
						pop + consume + prod
					}`;
				} else {
					params.STYLES = 'vi_emd_region_case';
					params.env = `formular:(m12*${pop}+m34*${consume}+m56*${prod})/${
						pop + consume + prod
					};case_return:${selectEmdLegend
						.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
						.join(',')}`;
				}
				layers.vi_emd.getSource().updateParams(params);
				layers.vi_emd.getSource().refresh();
			} else if (sggCode !== '') {
				// 시군구
				const params = {
					CQL_FILTER: `sido_cd in (${sidoCode})`,
				};
				if (!selectSggLegend.includes(false)) {
					params.STYLES = 'vi_sgg_region';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					}`;
				} else {
					params.STYLES = 'vi_sgg_region_case';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					};case_return:${selectSggLegend
						.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
						.join(',')}`;
				}
				layers.vi_sgg.getSource().updateParams(params);
				layers.vi_sgg.getSource().refresh();
			} else if (sidoCode !== '') {
				// 시도
				const params = {
					CQL_FILTER: `sido_cd like '%'`,
				};
				if (!selectSggLegend.includes(false)) {
					params.STYLES = 'vi_sgg_region';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					}`;
				} else {
					params.STYLES = 'vi_sgg_region_case';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					};case_return:${selectSggLegend
						.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
						.join(',')}`;
				}
				layers.vi_sgg.getSource().updateParams(params);
				layers.vi_sgg.getSource().refresh();
			} else {
				// 전국
				const params = {
					CQL_FILTER: `sido_cd like '%'`,
				};
				if (!selectSggLegend.includes(false)) {
					params.STYLES = 'vi_sgg_region';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					}`;
				} else {
					params.STYLES = 'vi_sgg_region_case';
					params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
						pop + consume + prod
					};case_return:${selectSggLegend
						.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
						.join(',')}`;
				}
				layers.vi_sgg.getSource().updateParams(params);
				layers.vi_sgg.getSource().refresh();
			}
		}
	}, [
		sidoCode,
		sggCode,
		emdCode,
		selectSggLegend,
		selectEmdLegend,
		weightedInfo,
	]);

	useEffect(() => {
		if (weightedMapLayers) {
			weightedMapLayers.vi_sgg.setOpacity(layerOpacity);
			weightedMapLayers.vi_emd.setOpacity(layerOpacity);
		}
	}, [layerOpacity]);

	const getSidoOptions = sidoList.map((cur, idx) => {
		return (
			<option key={cur.code} value={cur.code}>
				{cur.name}
			</option>
		);
	});

	const getSggOptions = sggList.map((cur, idx) => {
		return (
			<option key={cur.code} value={cur.code}>
				{cur.name}
			</option>
		);
	});

	const getEmdOptions = emdList.map((cur, idx) => {
		return (
			<option key={cur.code} value={cur.code}>
				{cur.name}
			</option>
		);
	});

	const extentFeatures = (features) => {
		const extent = features[0].getGeometry().getExtent();
		if (features.length > 1) {
			for (let i = 1; i < features.length; i++) {
				extend(extent, features[i].getGeometry().getExtent());
			}
		}
		const mapSize = weightedMap.getSize();
		weightedMap.getView().fit(extent, mapSize);
	};

	const handleChangeSidoCode = async (e) => {
		setSidoCode(e.target.value);
		setSidoName(e.target.options[e.target.selectedIndex].text);
		setSggCode('');
		setSggName('');
		setEmdCode('');
		setEmdName('');

		if (e.target.value === '') {
			weightedMap.getView().setCenter([14252145.350670181, 4388102.177922421]);
			weightedMap.getView().setZoom(7);
		} else {
			const features = await getFeatures(e.target.value, 'sido_cd');
			extentFeatures(features);
		}
	};

	const handleChangeSggCode = async (e) => {
		setSggCode(e.target.value);
		setSggName(e.target.options[e.target.selectedIndex].text);
		setEmdCode('');
		setEmdName('');
		if (e.target.value === '') {
			const features = await getFeatures(sidoCode, 'sido_cd');
			extentFeatures(features);
		} else {
			const features = await getFeatures(e.target.value, 'sgg_cd');
			extentFeatures(features);
		}
	};

	const handleChangeEmdCode = async (e) => {
		setEmdCode(e.target.value);
		setEmdName(e.target.options[e.target.selectedIndex].text);
		if (e.target.value === '') {
			const features = await getFeatures(sggCode, 'sgg_cd');
			extentFeatures(features);
		} else {
			const features = await getFeatures(e.target.value, 'amd_cd');
			extentFeatures(features);
		}
	};
	const onclickSubmitBtn = (e) => {
		if (!pop && !consume && !prod) {
			alert('가중치를 모두 0으로 줄수는 없습니다.');
		} else {
			setWeightedInfo({
				pop: pop,
				consume: consume,
				prod: prod,
				sidoCode: sidoCode,
				sggCode: sggCode,
				emdCode: emdCode,
			});
			setTabInfoArr((pre) => {
				let arr = pre;
				arr[selectedTab] = {
					pop: pop,
					consume: consume,
					prod: prod,
					sidoCode: sidoCode,
					sggCode: sggCode,
					emdCode: emdCode,
					sidoName: sidoName,
					sggName: sggName,
					emdName: emdName,
				};
				return arr;
			});
		}
	};

	const handleChangeInput = (e) => {
		if (
			e.nativeEvent.data !== undefined &&
			e.nativeEvent.data !== '.' &&
			!Number.isInteger(e.nativeEvent.data * 1)
		) {
			if (e.target.id === 'pop') e.target.value = pop;
			else if (e.target.id === 'consume') e.target.value = consume;
			else if (e.target.id === 'prod') e.target.value = prod;
		}
		if (e.target.value.length > 3) {
			if (e.target.id === 'pop') e.target.value = pop;
			else if (e.target.id === 'consume') e.target.value = consume;
			else if (e.target.id === 'prod') e.target.value = prod;
		}

		if (e.target.value > 10) e.target.value = 10;
	};

	const handleOpacity = (type, index) => {
		if (weightedMap) {
			if (type === 'sgg') {
				setSelectSggLegend((prev) => {
					const copy = [...prev];
					copy[index] = !prev[index];
					return copy;
				});
			} else if (type === 'emd') {
				setSelectEmdLegend((prev) => {
					const copy = [...prev];
					copy[index] = !prev[index];
					return copy;
				});
			}
		}
	};

	const legends = (object, state, type) => {
		return object.legend.map((legend, index) => {
			return (
				<button
					key={index}
					className="weightedLegendButton"
					style={
						state[index]
							? { background: object.color[index] }
							: { background: '#fff' }
					}
					onClick={() => handleOpacity(type, index)}
				>
					{legend}
				</button>
			);
		});
	};

	useEffect(() => {
		const params = {};
		const layers = getLayersByKey('weightedLayers');
		if (!selectSggLegend.includes(false)) {
			params.STYLES = 'vi_sgg_region';
			params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
				pop + consume + prod
			}`;
		} else {
			params.STYLES = 'vi_sgg_region_case';
			params.env = `formular:(l1_1*${pop}+l1_2*${consume}+l1_3*${prod})/${
				pop + consume + prod
			};case_return:${selectSggLegend
				.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
				.join(',')}`;
		}

		layers.vi_sgg.getSource().updateParams(params);
		layers.vi_sgg.getSource().refresh();
	}, [selectSggLegend]);

	useEffect(() => {
		const params = {};
		const layers = getLayersByKey('weightedLayers');

		if (!selectEmdLegend.includes(false)) {
			params.STYLES = 'vi_emd_region';
			params.env = `formular:(m12*${pop}+m34*${consume}+m56*${prod})/${
				pop + consume + prod
			}`;
		} else {
			params.STYLES = 'vi_emd_region_case';
			params.env = `formular:(m12*${pop}+m34*${consume}+m56*${prod})/${
				pop + consume + prod
			};case_return:${selectEmdLegend
				.reduce((acc, cur, idx) => (cur ? [...acc, idx + 1] : acc), [])
				.join(',')}`;
		}

		layers.vi_emd.getSource().updateParams(params);
		layers.vi_emd.getSource().refresh();
	}, [selectEmdLegend]);

	return (
		<div>
			<span className="weightedAreaTit">선택지역</span>
			<div className="weightedAreaController">
				<div className="selectBox">
					<label htmlFor="sido">시도</label>
					<select id="sido" value={sidoCode} onChange={handleChangeSidoCode}>
						<option value="">전국</option>
						{getSidoOptions}
					</select>
				</div>
				<div className="selectBox">
					<label htmlFor="sgg">시군구</label>
					<select id="sgg" value={sggCode} onChange={handleChangeSggCode}>
						<option value="">전체</option>
						{getSggOptions}
					</select>
				</div>
				<div className="selectBox">
					<label htmlFor="emd">읍면동</label>
					<select id="emd" value={emdCode} onChange={handleChangeEmdCode}>
						<option value="">전체</option>
						{getEmdOptions}
					</select>
				</div>
			</div>
			<div className="dotLine" />
			<span className="weightedValueTit">가중치</span>
			<div className="weightedValueController">
				<div className="inputBox">
					<label htmlFor="pop">인구활력도</label>
					<input
						id="pop"
						type="number"
						value={pop}
						step={0.1}
						onChange={(e) => setPop(e.target.value * 1)}
						max={10}
						min={0}
						onInput={(e) => handleChangeInput(e)}
					/>
				</div>
				<div className="inputBox">
					<label htmlFor="consume">소비활력도</label>
					<input
						id="consume"
						type="number"
						value={consume}
						step={0.1}
						onChange={(e) => setConsume(e.target.value * 1)}
						max={10}
						min={0}
						onInput={(e) => handleChangeInput(e)}
					/>
				</div>
				<div className="inputBox">
					<label htmlFor="prod">생산활력도</label>
					<input
						id="prod"
						type="number"
						value={prod}
						step={0.1}
						onChange={(e) => setProd(e.target.value * 1)}
						max={10}
						min={0}
						onInput={(e) => handleChangeInput(e)}
					/>
				</div>
			</div>
			<div className="submitBtnBox">
				<div className="TooltipBox">
					<button
						className="inputSubmitBtn "
						name="inputSubmit"
						alt="적용하기"
						onClick={onclickSubmitBtn}
					>
						적용하기
					</button>
					<span className="TooltipText">
						시도와 시군구를 선택한 후에 가중치를 적용할 수 있습니다.
					</span>
				</div>
			</div>
			<div className="dotLine" />

			<div className="weightedLegendController">
				{emdCode === ''
					? legends(quantileSggData, selectSggLegend, 'sgg')
					: legends(quantileEmdData, selectEmdLegend, 'emd')}
			</div>
			<button
				className="legendSelctAllBtn"
				name="inputSubmit"
				alt="범례 전체 선택"
				onClick={() => {
					if (emdCode) {
						setSelectEmdLegend(new Array(10).fill(true));
					} else {
						setSelectSggLegend(new Array(5).fill(true));
					}
				}}
			>
				범례 전체 선택
			</button>
			<span className="opacitySliderTit">투명도 조절</span>
			<div className="sliderBox">
				<Slider
					min={0}
					max={1}
					step={0.1}
					value={layerOpacity}
					onChange={(e) => {
						setLayerOpacity(e.target.value);
					}}
				/>
			</div>
		</div>
	);
}

export default WeightedContent;
